import type { GamePointerEvent, PointerPhase } from './PointerEvent';

type PointerHandler = (e: GamePointerEvent) => void;
type InputChannel = PointerPhase | 'pinch';

interface PointerInfo {
  clientX: number;
  clientY: number;
}

export class InputManager {
  private readonly handlers = new Map<InputChannel, Set<PointerHandler>>();
  private readonly activePointers = new Map<number, PointerInfo>();
  private lastPinchDistance: number | null = null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.attach();
  }

  on(channel: InputChannel, handler: PointerHandler): void {
    let set = this.handlers.get(channel);
    if (!set) {
      set = new Set();
      this.handlers.set(channel, set);
    }
    set.add(handler);
  }

  off(channel: InputChannel, handler: PointerHandler): void {
    this.handlers.get(channel)?.delete(handler);
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onDown);
    this.canvas.removeEventListener('pointermove', this.onMove);
    this.canvas.removeEventListener('pointerup', this.onUp);
    this.canvas.removeEventListener('pointercancel', this.onCancel);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.activePointers.clear();
  }

  private attach(): void {
    this.canvas.addEventListener('pointerdown', this.onDown, { passive: false });
    this.canvas.addEventListener('pointermove', this.onMove, { passive: false });
    this.canvas.addEventListener('pointerup', this.onUp, { passive: false });
    this.canvas.addEventListener('pointercancel', this.onCancel, { passive: false });
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  private readonly onDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.activePointers.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
    this.lastPinchDistance = null;
    this.emit('start', e);
  };

  private readonly onMove = (e: PointerEvent): void => {
    e.preventDefault();
    this.activePointers.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

    if (this.activePointers.size === 2) {
      const [a, b] = [...this.activePointers.values()] as [PointerInfo, PointerInfo];
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (this.lastPinchDistance !== null) {
        const pinchDelta = dist - this.lastPinchDistance;
        const evt = this.buildEvent(e, 'move');
        evt.pinchDelta = pinchDelta;
        this.dispatch('pinch', evt);
      }
      this.lastPinchDistance = dist;
    } else {
      this.lastPinchDistance = null;
      this.emit('move', e);
    }
  };

  private readonly onUp = (e: PointerEvent): void => {
    e.preventDefault();
    this.activePointers.delete(e.pointerId);
    this.lastPinchDistance = null;
    this.emit('end', e);
  };

  private readonly onCancel = (e: PointerEvent): void => {
    e.preventDefault();
    this.activePointers.delete(e.pointerId);
    this.lastPinchDistance = null;
    this.emit('cancel', e);
  };

  private readonly onContextMenu = (e: Event): void => {
    e.preventDefault();
  };

  private emit(phase: PointerPhase, e: PointerEvent): void {
    this.dispatch(phase, this.buildEvent(e, phase));
  }

  private dispatch(channel: InputChannel, evt: GamePointerEvent): void {
    this.handlers.get(channel)?.forEach((h) => h(evt));
  }

  private buildEvent(e: PointerEvent, phase: PointerPhase): GamePointerEvent {
    const rect = this.canvas.getBoundingClientRect();
    const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    return {
      phase,
      pointerId: e.pointerId,
      ndcX,
      ndcY,
      screenX: e.clientX,
      screenY: e.clientY,
    };
  }
}
